import { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useSsoLoginMutation } from "../api/auth";
import { useEstablishSession } from "../hooks/useEstablishSession";
import LoadingPage from "../components/LoadingPage";

/** postMessage type the host page sends when it embeds the panel in an iframe. */
const SSO_TOKEN_MESSAGE = "icont:sso-token";
/** postMessage type we send upward to ask the host for a token. */
const SSO_READY_MESSAGE = "icont:sso-ready";

/**
 * Only same-origin, absolute paths are honoured as a post-login destination, so
 * a crafted `?redirect=` cannot bounce the agent to another site.
 */
function safeRedirect(target: string | null): string {
  if (!target || !target.startsWith("/") || target.startsWith("//")) return "/";
  return target;
}

/**
 * Single sign-on entry point.
 *
 * The agent is already authenticated in the customer's own panel; the host
 * backend mints a short-lived token with the company's tenant_secret and hands
 * it to us one of two ways:
 *
 *   redirect  — `/sso?token=...`, the host links here from its sidebar
 *   iframe    — the host embeds `/sso` and posts the token in, keeping it out
 *               of the URL (and therefore out of history and referrer headers)
 *
 * Both end in the same place: exchange the token for a session, then hand off
 * to the normal dashboard.
 */
export default function SsoLoginPage() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { t } = useTranslation("auth");
  const [ssoLogin] = useSsoLoginMutation();
  const { establishSession, setIsAuthenticated } = useEstablishSession();

  const [error, setError] = useState<string | null>(null);
  const [status, setStatus] = useState<"waiting" | "connecting">("connecting");

  // The exchange must run once. Both the query-param path and the postMessage
  // path can fire during the same mount (and React 18 StrictMode double-invokes
  // effects in dev), and a replayed token is exactly what the backend rejects.
  const exchangeStarted = useRef(false);

  const signIn = useCallback(
    async (token: string) => {
      if (exchangeStarted.current) return;
      exchangeStarted.current = true;

      setStatus("connecting");
      setError(null);

      try {
        const response = await ssoLogin({ token }).unwrap();

        // The MSRP digest credential is bound to `agent_username` — that is the
        // name the gateway hashed into HA1 (see buildHa1Password callers) and
        // the name SCont will check the digest against. Anything else, such as
        // the agent's email or the host site's own user id, fails the handshake.
        const result = await establishSession(response, {
          username: response.agent_username,
          secret: response.msrp_ha1,
        });

        if (result === "reloading") return;

        navigate(safeRedirect(searchParams.get("redirect")), { replace: true });
      } catch (err) {
        console.error("SSO login failed:", err);
        setIsAuthenticated(false);
        setError(t("sso.failed"));
      }
    },
    [ssoLogin, establishSession, navigate, searchParams, setIsAuthenticated, t],
  );

  useEffect(() => {
    const tokenFromUrl = searchParams.get("token");
    if (tokenFromUrl) {
      // Drop the token from the address bar before doing anything with it. It
      // is single-use so a leaked copy is not catastrophic, but there is no
      // reason for it to sit in browser history, in a screenshot, or in the
      // Referer of any request this page makes.
      const cleaned = new URL(window.location.href);
      cleaned.searchParams.delete("token");
      window.history.replaceState({}, "", cleaned.toString());

      signIn(tokenFromUrl);
      return;
    }

    // No token in the URL: we are embedded, so ask the host for one.
    //
    // The inbound origin is deliberately not filtered. The token is not a
    // bearer credential we mint — it is HMAC-signed with the tenant_secret and
    // verified server-side, so a hostile framer can only post something the
    // gateway rejects. We never post anything sensitive back out; the outbound
    // "ready" ping carries no data.
    if (window.parent === window) {
      setError(t("sso.missingToken"));
      return;
    }

    setStatus("waiting");

    const onMessage = (event: MessageEvent) => {
      const data = event.data;
      if (!data || data.type !== SSO_TOKEN_MESSAGE || typeof data.token !== "string") return;
      signIn(data.token);
    };

    window.addEventListener("message", onMessage);
    window.parent.postMessage({ type: SSO_READY_MESSAGE }, "*");

    return () => window.removeEventListener("message", onMessage);
  }, [searchParams, signIn, t]);

  if (error) {
    return (
      <div className="flex h-screen flex-col items-center justify-center gap-4 px-6 text-center">
        <img alt="ICONT" src="/images/icont.png" className="h-12" />
        <p className="max-w-md text-gray-600 dark:text-gray-300">{error}</p>
        <button
          onClick={() => navigate("/login")}
          className="text-primary-600 dark:text-primary-300"
        >
          {t("sso.manualLogin")}
        </button>
      </div>
    );
  }

  return (
    <div className="flex h-screen flex-col items-center justify-center gap-4">
      <LoadingPage />
      <p className="text-gray-600 dark:text-gray-300">
        {status === "waiting" ? t("sso.waitingForToken") : t("sso.connecting")}
      </p>
    </div>
  );
}
